// ---------------------------------------------------------------------------
// ErrorBoundary — Catches render-phase errors in child components.
//
// Prevents a single page crash from taking down the entire shell.
// Wraps each page inside Layout so the shell remains functional.
// ---------------------------------------------------------------------------

import {
  Button,
  Card,
  Subtitle1,
  Body1,
  tokens,
} from '@fluentui/react-components';
import { Component, type ErrorInfo } from 'react';

import type {
  ErrorBoundaryProps,
  ErrorBoundaryState,
} from './ErrorBoundary.interface';

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  override render() {
    if (this.state.error) {
      return (
        <Card
          style={{
            maxWidth: 480,
            margin: 'auto',
            marginTop: tokens.spacingVerticalXXXL,
            padding: tokens.spacingHorizontalXL,
          }}
        >
          <Subtitle1>Something went wrong</Subtitle1>
          <Body1 style={{ color: tokens.colorNeutralForeground2 }}>
            {this.state.error.message}
          </Body1>
          <Button
            appearance="primary"
            onClick={() => this.setState({ error: null })}
            style={{ marginTop: tokens.spacingVerticalM }}
          >
            Try again
          </Button>
        </Card>
      );
    }

    return this.props.children;
  }
}
